import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import QuickView from "@/components/QuickView";
import { api } from "@/lib/api";

const HERO_IMG = "/hero-campaign.webp";
const STORY_IMG =
  "https://images.unsplash.com/photo-1646191152321-673e8b541cb8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format";
const LOOK_TEASER =
  "https://images.unsplash.com/photo-1768809248811-a6d55743a874?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800&auto=format";

const lineReveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const DROP_DATE = new Date("2026-11-13T18:00:00+01:00").getTime();

function DropCountdown() {
  const [now, setNow] = useState(Date.now());
  const [email, setEmail] = useState("");
  const [alertState, setAlertState] = useState("idle");
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, DROP_DATE - now);
  const units = [
    { v: Math.floor(diff / 86400000), l: "Jours" },
    { v: Math.floor(diff / 3600000) % 24, l: "Heures" },
    { v: Math.floor(diff / 60000) % 60, l: "Min" },
    { v: Math.floor(diff / 1000) % 60, l: "Sec" },
  ];

  const notify = async (e) => {
    e.preventDefault();
    if (!email) return;
    setAlertState("loading");
    try {
      const res = await api.post("/drop-alert", { email });
      toast.success(
        res.data.message === "already registered"
          ? "Vous êtes déjà sur la liste du drop."
          : "Noté. Un email à l’ouverture du drop."
      );
      setAlertState("done");
      setEmail("");
    } catch {
      toast.error("Impossible pour l’instant. Réessayez.");
      setAlertState("idle");
    }
  };

  return (
    <div data-testid="drop-countdown">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-zinc-500">
          Volume 2 — Drop
        </span>
        {diff <= 0 ? (
          <span className="font-mono text-sm uppercase tracking-[0.3em] text-zinc-100">
            Disponible maintenant
          </span>
        ) : (
          <div className="flex items-baseline gap-4">
            {units.map((u) => (
              <span key={u.l} className="flex items-baseline gap-1.5">
                <span className="font-display text-2xl font-bold tabular-nums text-zinc-100">
                  {String(u.v).padStart(2, "0")}
                </span>
                <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">
                  {u.l}
                </span>
              </span>
            ))}
          </div>
        )}
      </div>
      {diff > 0 && (
        <form onSubmit={notify} className="mt-5 flex max-w-sm">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            data-testid="drop-alert-email-input"
            className="h-11 flex-1 border border-white/10 bg-coal px-4 font-mono text-[11px] tracking-wider text-zinc-100 placeholder:text-zinc-600 focus:border-white/50 focus:outline-none"
          />
          <button
            type="submit"
            disabled={alertState === "loading"}
            data-testid="drop-alert-button"
            className="h-11 border border-white/20 px-5 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-200 transition-colors duration-300 hover:border-white hover:text-white disabled:opacity-60"
          >
            {alertState === "loading" ? "..." : "Me prévenir"}
          </button>
        </form>
      )}
    </div>
  );
}

function HeroLine({ children, index, className }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        className={`block ${className}`}
        custom={index}
        variants={lineReveal}
        initial="hidden"
        animate="show"
      >
        {children}
      </motion.span>
    </span>
  );
}

function BentoCell({ product, span, onQuickView }) {
  return (
    <Reveal className={span}>
      <div
        data-testid={`bento-product-${product.id}`}
        onClick={() => onQuickView(product)}
        className="group relative aspect-[4/5] cursor-pointer overflow-hidden border border-white/5 bg-coal lg:aspect-auto lg:h-[70vh]"
      >
        <img
          src={`${product.image}&w=1400&auto=format`}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
        />
        {product.tag && (
          <span className="absolute left-5 top-5 border border-ember/60 bg-ink/70 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.3em] text-ember backdrop-blur-md">
            {product.tag}
          </span>
        )}
        <div className="absolute inset-x-0 bottom-0 flex translate-y-2 items-end justify-between bg-ink/70 px-6 py-5 opacity-0 backdrop-blur-md transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
          <div>
            <p className="font-display text-lg font-semibold uppercase leading-tight">{product.name}</p>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400">
              {product.category} — €{product.price.toFixed(0)}
            </p>
          </div>
          <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/20 text-zinc-100 transition-colors duration-300 group-hover:border-white group-hover:text-white">
            <ArrowUpRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Reveal>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  const [quickView, setQuickView] = useState(null);
  const heroRef = useRef(null);
  const storyRef = useRef(null);

  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImgY = useTransform(heroProgress, [0, 1], ["0%", "22%"]);
  const heroTextY = useTransform(heroProgress, [0, 1], ["0%", "45%"]);

  const { scrollYProgress: storyProgress } = useScroll({
    target: storyRef,
    offset: ["start end", "end start"],
  });
  const storyImgY = useTransform(storyProgress, [0, 1], ["-12%", "12%"]);

  useEffect(() => {
    api.get("/products").then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const featured = products.slice(0, 4);
  const heroProduct = products.find((p) => p.id === "mld-001") || products[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden">
        <div className="mx-auto grid max-w-[1700px] grid-cols-1 items-center gap-10 px-5 pb-16 pt-32 sm:px-8 lg:min-h-screen lg:grid-cols-12 lg:gap-6 lg:px-16 lg:pb-0 lg:pt-20">
          <motion.div style={{ y: heroTextY }} className="relative z-10 lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="font-mono text-[10px] uppercase tracking-[0.35em] text-zinc-500"
              data-testid="hero-meta"
            >
              Paris — Collection 001 · Résistance
            </motion.p>

            <h1 className="mt-6 font-blackletter text-[17vw] leading-[0.95] tracking-normal sm:text-[14vw] lg:text-[8.5vw]" data-testid="hero-headline">
              <HeroLine index={0} className="text-zinc-100">Malgré</HeroLine>
              <HeroLine index={1} className="text-zinc-100">Les</HeroLine>
              <HeroLine index={2} className="text-zinc-100">Difficultés.</HeroLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-8 max-w-md font-serif text-xl italic leading-relaxed text-zinc-400 sm:text-2xl"
            >
              « Despite the difficulties — we move. » Luxury streetwear cut in Paris, worn everywhere the night
              reaches.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/collections"
                data-testid="hero-shop-cta"
                className="group flex h-14 items-center gap-3 bg-white px-8 font-mono text-[11px] uppercase tracking-[0.3em] text-ink transition-colors duration-300 hover:bg-zinc-300"
              >
                Shop the drop
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/lookbook"
                data-testid="hero-lookbook-cta"
                className="flex h-14 items-center border border-white/15 px-8 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-200 transition-colors duration-300 hover:border-ember hover:text-ember"
              >
                View lookbook
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.9 }}
              className="mt-12 border-t border-white/5 pt-6"
            >
              <DropCountdown />
            </motion.div>
          </motion.div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.5, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative h-[60vh] overflow-hidden lg:h-[78vh]"
              style={{ clipPath: "polygon(0 3%, 100% 0, 100% 97%, 0 100%)" }}
            >
              <motion.img
                src={HERO_IMG}
                alt="MLD campaign — model in dark luxury outerwear"
                style={{ y: heroImgY }}
                className="h-[115%] w-full scale-110 object-cover"
              />
              <div className="absolute inset-0 bg-black/20" />
            </motion.div>

            {heroProduct && (
              <motion.button
                data-testid="hero-floating-product"
                onClick={() => setQuickView(heroProduct)}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4, duration: 0.8 }}
                className="absolute -bottom-2 left-0 flex animate-floaty items-center gap-4 border border-white/10 bg-ink/80 p-4 text-left backdrop-blur-xl lg:-left-16"
              >
                <img
                  src={`${heroProduct.image}&w=200&auto=format`}
                  alt=""
                  className="h-16 w-14 object-cover"
                />
                <span>
                  <span className="block font-mono text-[9px] uppercase tracking-[0.3em] text-ember">
                    Pièce 001
                  </span>
                  <span className="mt-1 block font-display text-sm font-semibold uppercase leading-tight">
                    {heroProduct.name}
                  </span>
                  <span className="mt-1 block font-mono text-xs text-zinc-400">
                    €{heroProduct.price.toFixed(0)}
                  </span>
                </span>
              </motion.button>
            )}
          </div>
        </div>

      </section>

      <Marquee />

      {/* THE DROP */}
      <section className="mx-auto max-w-[1700px] px-5 py-24 sm:px-8 lg:px-16 lg:py-36" data-testid="drop-section">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">Drop / 001</p>
            <h2 className="mt-4 font-blackletter text-5xl leading-[1] sm:text-7xl lg:text-8xl">
              Volume 1
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link
              to="/collections"
              data-testid="drop-view-all-link"
              className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-400 transition-colors duration-300 hover:text-ember"
            >
              View all pieces
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-4 lg:grid-cols-12 lg:gap-6">
          {featured[0] && <BentoCell product={featured[0]} span="lg:col-span-7" onQuickView={setQuickView} />}
          {featured[1] && <BentoCell product={featured[1]} span="lg:col-span-5" onQuickView={setQuickView} />}
          {featured[2] && <BentoCell product={featured[2]} span="lg:col-span-5" onQuickView={setQuickView} />}
          {featured[3] && <BentoCell product={featured[3]} span="lg:col-span-7" onQuickView={setQuickView} />}
        </div>
      </section>

      {/* STORY TEASER */}
      <section ref={storyRef} className="border-t border-white/5 bg-coal" data-testid="story-teaser">
        <div className="mx-auto grid max-w-[1700px] grid-cols-1 items-center gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:px-16 lg:py-40">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">La Maison</p>
              <p className="mt-8 font-serif text-3xl italic leading-snug text-zinc-200 sm:text-4xl lg:text-5xl">
                « Born from the struggle. <span className="text-gold">Tailored</span> for the victory lap. »
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-10 max-w-xl text-sm leading-relaxed text-zinc-400 sm:text-base">
                Malgré les difficultés, c’est une marque qui mise sur une histoire derrière chaque vêtement.
                Vous n’achetez pas simplement des vêtements, vous achetez des émotions.
              </p>
              <Link
                to="/about"
                data-testid="story-read-more-link"
                className="group mt-10 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-200 transition-colors duration-300 hover:text-ember"
              >
                Read the manifesto
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="relative h-[50vh] overflow-hidden lg:h-[60vh]">
              <motion.img
                src={STORY_IMG}
                alt="Studio portrait in dramatic light"
                style={{ y: storyImgY }}
                loading="lazy"
                className="h-[124%] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* LOOKBOOK TEASER */}
      <section className="mx-auto max-w-[1700px] px-5 py-24 sm:px-8 lg:px-16 lg:py-36" data-testid="lookbook-teaser">
        <Reveal>
          <Link to="/lookbook" data-testid="lookbook-teaser-link" className="group relative block overflow-hidden border border-white/5">
            <img
              src={LOOK_TEASER}
              alt="Lookbook campaign"
              loading="lazy"
              className="h-[60vh] w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 lg:h-[75vh]"
            />
            <div className="absolute inset-0 bg-black/45 transition-colors duration-700 group-hover:bg-black/30" />
            <div className="absolute inset-x-0 bottom-0 flex flex-wrap items-end justify-between gap-6 p-7 sm:p-12">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-ember">Lookbook</p>
                <h3 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight sm:text-5xl">
                  Trois Actes
                </h3>
              </div>
              <span className="flex h-14 w-14 items-center justify-center border border-white/30 text-zinc-100 transition-all duration-500 group-hover:border-white group-hover:bg-white group-hover:text-ink">
                <ArrowUpRight className="h-5 w-5" />
              </span>
            </div>
          </Link>
        </Reveal>
      </section>

      <AnimatePresence>
        {quickView && <QuickView product={quickView} onClose={() => setQuickView(null)} />}
      </AnimatePresence>
    </div>
  );
}
